import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import moment from "moment";

import useMutation from "src/hooks/useMutation";
import useQueryParams from "src/hooks/useQueryParams";
import useNotification from "src/hooks/notification";
import {
  CREATE_SHIPMENT_MUTATION,
  ADD_SHIPMENT_ITEMS_MUTATION,
} from "src/constants/graphql/mutations";
import { serverFormatSeconds } from "src/utils/dates";
import { fullWidthDialog } from "src/constants/dialog";

import Step0 from "./Step0";
import Step1 from "./Step1";
import Step2 from "./Step2";

export default function OnsiteCaptureWizard({
  open,
  onClose,
  selectedIds,
  projectId,
  unSelectBulk,
}) {
  const navigate = useNavigate();
  const queryParams = useQueryParams();
  const { notifyError } = useNotification();
  const step = parseInt(queryParams.get("onsiteWizardStep") || "0", 10);

  const [progress, setProgress] = useState(0);
  const runningRef = useRef(false);

  const onsiteShipmentId = queryParams.get("onsiteShipmentId");
  const onsiteShipmentSid = queryParams.get("onsiteShipmentSid");

  const [createShipment] = useMutation(CREATE_SHIPMENT_MUTATION);
  const [addShipmentItem] = useMutation(ADD_SHIPMENT_ITEMS_MUTATION);

  const totalItems = selectedIds.length;

  const updateParams = updates => {
    const params = new URLSearchParams(window.location.search);
    Object.entries(updates).forEach(([key, value]) => {
      if (value === null) {
        params.delete(key);
      } else {
        params.set(key, value);
      }
    });
    navigate({ search: params.toString() }, { replace: true });
  };

  const setStep = newStep => {
    updateParams({ onsiteWizardStep: newStep });
  };

  const handleStart = async () => {
    runningRef.current = true;
    setStep(1);
    setProgress(0);
    let createdShipment = false;

    try {
      const createRes = await createShipment({
        variables: {
          input: {
            projectId: parseInt(projectId),
            startTime: moment(new Date()).format(serverFormatSeconds),
            draft: false,
          },
        },
      });

      const shipment = createRes.data.createShipment.shipment;
      const shipmentId = shipment.id;
      createdShipment = true;

      updateParams({
        onsiteShipmentId: shipment.id,
        onsiteShipmentSid: shipment.shipmentId,
      });

      const itemIds = Array.from(selectedIds);

      for (let i = 0; i < itemIds.length; i++) {
        await addShipmentItem({
          variables: {
            assetId: itemIds[i],
            shipmentId,
            quantity: 0,
          },
        });
        setProgress(Math.round(((i + 1) / itemIds.length) * 100));
      }

      updateParams({ onsiteWizardStep: "2" });
    } catch (error) {
      notifyError(error.message || "Failed to create the receiving report");
      // A failed creation has no report to resume; a failure while adding items
      // does, so preserve its URL state and show the summary instead of leaving
      // the user stranded on the in-progress screen.
      setStep(createdShipment ? "2" : "0");
    } finally {
      runningRef.current = false;
    }
  };

  useEffect(() => {
    if (step !== 1 || runningRef.current) return;
    setStep(onsiteShipmentId ? "2" : "0");
  }, [step, onsiteShipmentId]);

  const handleClose = () => {
    if (step === 2 && unSelectBulk) {
      unSelectBulk();
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth={false}
      onClose={handleClose}
      sx={{ ...fullWidthDialog, "& .MuiDialog-paper": { maxWidth: 500 } }}
    >
      <DialogContent sx={{ textAlign: "center", px: 3 }}>
        {step === 0 && <Step0 onStart={handleStart} />}
        {step === 1 && <Step1 progress={progress} totalItems={totalItems} />}
        {step === 2 && onsiteShipmentId && (
          <Step2
            id={onsiteShipmentId}
            shipmentId={onsiteShipmentSid}
            projectId={projectId}
            onClose={handleClose}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
